export const FilterBlockDocumentationMarkdown = `# Filtering

## What does this block do?

The Filter List block allows you to isolate one or more items from a list based on specified conditions. The list can also be sorted, or constrained to return a limited number of items.

## When to use it?

Many blocks return a big number of items, such as all encounters for a patient, all lab results, all orders from a referral. Filter List allows you to target the specific item to pass to the subsequent blocks.

- Use this block to narrow down a large list of items to only those that meet specific criteria.
- Employ it to sort data (e.g., sort encounters by appointment date).
- Integrate it into workflows that require dynamic filtering based on real-time data from previous steps.

## How to configure this block

1. Specify where the items are in the **List to filter** field. This payload from the prior block needs to be in a form of a list. The example below comes from a block called "Patient", and the list to filter is located at \`{{Patient.data.appointments}}\`.

Example Input:

~~~json
{
  "appointments": [
    {
      "appointmentDate": "2024-03-20",
      "appointmentType": "new-patient",
      "department": "orthopedics"
    },
    {
      "appointmentDate": "2024-03-15",
      "appointmentType": "follow-up",
      "department": "cardiology"
    },
    {
      "appointmentDate": "2024-03-18",
      "appointmentType": "follow-up",
      "department": "neurology"
    }
  ]
}
~~~

2. Once you have selected a list you can provide one or more criteria in the **Conditions match**. Click on "Add Condition" and enter the name of the field which you will use for filtering. In the example above, it may be \`appointmentType\` which is a text \`string\` and for which you could enter \`equals\` "follow-up" resulting in the bottom two appointments getting selected.

## Sorting:

This block also allows you to sort items. You can pick to sort dates chronologically or text alphabetically. Since we have already specified \`{{Patient.data.appointments}}\` as the list, we just need to point to **Field to sort** like \`appointmentDate\` as **ascending** means that items would be in chronological "oldest to newest," "2022, 2023, 2024" or **descending**, reverse-chronological "2024, 2023, 2022".

## Limiting:

Sometimes you will want to return a certain number of items "3 latest labs" "most recent appointment", this is where you can set a limit as to the number of items this block returns.

- Return all - every item matching filters above will be returned
- Return N items - allows you to set the number
- First match - returns just the first result

## How to test

1. **Prepare the Environment**:
  - Ensure that the upstream block provides a list of items to filter.
2. **Configure Filtering Conditions**:
  - If you'd like, add one or more conditions to narrow down the list.
3. **Simulate a Test Run**:
  - Enable "Mock Output for Test Run" to validate the configuration without processing live data.
4. **Execute the Flow**:
  - Run the workflow and verify that the list is filtered, sorted, and limited as expected.
5. **Validate Output**:
  - Check that the returned list meets all specified conditions.

## Best practices

- **Use Dynamic References**:
  - Leverage the \`{{…}}\` notation to create flexible, context-aware filtering conditions.
- **Combine Multiple Conditions**:
  - Use multiple conditions to create precise filtering criteria.
- **Test Edge Cases**:
  - Verify the block's behavior with empty lists, lists with no matching items, and various data types.
- **Understand Sorting and Limiting**:
  - Carefully configure sorting and return type to get the desired output.
- **Mock Testing**:
  - Use the mock output feature to prototype and validate your filtering logic.
`;
